import * as Popover from "@radix-ui/react-popover";
import {
  CheckCircle,
  Clock,
  Columns3,
  Layers,
  LayoutGrid,
  List,
  Play,
  Plus,
  SlidersHorizontal,
  Table2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  CampoDeBusqueda,
  DataTable,
  FilterBar,
  PageHeader,
  Paginacion,
  TarjetasDeEstado,
  type Vista,
} from "../../components/ui/patrones";
import { Aviso, Boton } from "../../components/ui/primitivos";
import { cn } from "../../lib/cn";
import {
  asignarOrden,
  cambiarEstadoOrden,
  fetchImpresoras,
  fetchOrdenes,
  fetchUsuarios,
  type OrdenListada,
} from "../../lib/consola-api";
import { type Orden, siguienteOrden } from "../../lib/orden";
import { paginar, paginaValida } from "../../lib/paginacion";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { exigeMotivo } from "./cambio-de-estado";
import { DialogoCambioDeEstado } from "./DialogoCambioDeEstado";
import { ESTADOS_DEL_FILTRO, etiquetaDeEstado, GRUPOS, TONO_DE_GRUPO } from "./estados";
import { EstadoInline, ImpresoraInline, ResponsableInline } from "./inline";
import { Kanban } from "./Kanban";
import { coincideConFiltros } from "./tablero";
import { SinResultados, VistaLista, VistaTarjetas } from "./vistas";

/**
 * El menú del engranaje, porteado del `DropdownMenu` de `imprenta`.
 *
 * Junta las dos cosas que son **de la vista y no del dato**: con qué forma se
 * mira el tablero y si las tarjetas de resumen están o no. Estaban sueltas —el
 * selector de vista vivía entre los filtros, donde se lee como uno más, y las
 * tarjetas no se podían esconder.
 */
function MenuDeOpciones({
  vista,
  alCambiarVista,
  mostrarTarjetas,
  alCambiarTarjetas,
}: {
  vista: Vista;
  alCambiarVista: (v: Vista) => void;
  mostrarTarjetas: boolean;
  alCambiarTarjetas: (v: boolean) => void;
}) {
  const [abierto, setAbierto] = useState(false);

  return (
    <Popover.Root open={abierto} onOpenChange={setAbierto}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="Opciones de vista"
          className="flex h-9 w-9 items-center justify-center rounded-md border border-neutral-300 bg-white text-neutral-600 transition-colors hover:bg-neutral-50 hover:text-neutral-900"
        >
          <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="z-50 w-56 rounded-lg border border-neutral-200 bg-white p-2 shadow-lg"
        >
          <p className="px-2 pb-1 font-semibold text-neutral-900 text-sm">Opciones de Vista</p>
          <div className="my-1 h-px bg-neutral-200" />
          <p className="px-2 py-1 text-neutral-500 text-xs">Modo de visualización</p>
          {VISTAS.map((v) => (
            <button
              key={v.clave}
              type="button"
              onClick={() => alCambiarVista(v.clave)}
              aria-pressed={vista === v.clave}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                vista === v.clave
                  ? "bg-brand-50 text-brand-700"
                  : "text-neutral-800 hover:bg-neutral-100",
              )}
            >
              <v.icono className="h-4 w-4" aria-hidden="true" />
              {v.texto}
            </button>
          ))}
          <div className="my-1 h-px bg-neutral-200" />
          <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-neutral-800 text-sm hover:bg-neutral-100">
            <input
              type="checkbox"
              checked={mostrarTarjetas}
              onChange={(e) => alCambiarTarjetas(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-300"
            />
            Mostrar Dashboard
          </label>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * Tablero de producción — porteado de `production` de `imprenta`.
 *
 * **Sin polling** (ADR 0006, invariante 11): los datos se piden al entrar y
 * después de cada acción. El refresco en vivo llega con el Durable Object, no
 * con un `setInterval` que impediría a Neon suspender.
 *
 * Lo que se ve ya viene filtrado del worker: sin `produccion.read_all`, un
 * operario recibe solo sus OTs, y sin `produccion.precios` los importes vienen
 * en `null`. Acá no se filtra de nuevo — eso daría la ilusión de que la UI
 * protege algo.
 */

/**
 * Preferencias del tablero, en `localStorage` como en `imprenta`.
 *
 * La vista y el orden son de la PERSONA, no de la sesión: quien trabaja todo el
 * día con esto lo deja como lo dejó. Los filtros no se guardan a propósito —
 * volver y encontrar un listado filtrado sin recordar por qué es peor que
 * volver a filtrar.
 */
const CLAVE_PREFS = "tablero-produccion";

/** Diez por página, como `imprenta`. */
const POR_PAGINA = 10;

/** Las cuatro vistas, con el nombre y el ícono que usa `imprenta`. */
const VISTAS = [
  { clave: "kanban" as Vista, texto: "Tablero", icono: LayoutGrid },
  { clave: "tarjetas" as Vista, texto: "Cards", icono: Columns3 },
  { clave: "lista" as Vista, texto: "Lista", icono: List },
  { clave: "tabla" as Vista, texto: "Tabla", icono: Table2 },
];

interface Preferencias {
  vista: Vista;
  orden: Orden | null;
  /** Si se muestran las tarjetas de resumen. `imprenta` deja esconderlas. */
  mostrarTarjetas: boolean;
}

function leerPreferencias(): Preferencias {
  try {
    const guardado = localStorage.getItem(CLAVE_PREFS);
    if (guardado)
      return { vista: "tabla", orden: null, mostrarTarjetas: true, ...JSON.parse(guardado) };
  } catch {
    // JSON corrupto: se ignora y se arranca con los valores por defecto. No
    // vale la pena romper el tablero por una preferencia.
  }
  return { vista: "tabla", orden: null, mostrarTarjetas: true };
}

export function TableroPagina() {
  const { can } = useSession();
  const navegar = useNavigate();
  const [estado, setEstado] = useState("");
  const [grupo, setGrupo] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState("");
  // Los filtros NO se persisten, a diferencia del de impresora de `imprenta`:
  // volver y encontrar un listado filtrado sin recordar por qué es peor que
  // volver a filtrar (decisión de Pedro, 2026-08-22 — ADR 0063).
  const [impresoraFiltro, setImpresoraFiltro] = useState("");
  const [pagina, setPagina] = useState(1);
  const [prefs, setPrefs] = useState<Preferencias>(leerPreferencias);

  useEffect(() => {
    localStorage.setItem(CLAVE_PREFS, JSON.stringify(prefs));
  }, [prefs]);

  // Una sola vez para toda la tabla: el select de responsable los necesita en
  // cada fila, y pedirlos por fila sería una consulta por OT.
  const usuarios = useDatos(() =>
    can("usuarios", "read") ? fetchUsuarios() : Promise.resolve([]),
  );

  // Igual que los usuarios, y sólo las activas: el select de la fila no debe
  // ofrecer un equipo dado de baja.
  const impresoras = useDatos(() =>
    can("impresoras", "read") ? fetchImpresoras() : Promise.resolve([]),
  );

  async function recargar() {
    setError(null);
    await ordenes.recargar();
  }

  /** Una mutación desde la fila: si falla, el error se ve arriba del tablero. */
  async function accionFila(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await ordenes.recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    }
  }
  const ordenes = useDatos(() => fetchOrdenes(estado), estado);
  const [error, setError] = useState<string | null>(null);
  /** Cancelar desde BORRADOR pide motivo también en el kanban (ADR 0098 B14). */
  const [cancelando, setCancelando] = useState<{ orden: OrdenListada; destino: string } | null>(
    null,
  );

  /**
   * Abrir una OT es NAVEGAR, no montar un modal (ADR 0064).
   *
   * Con una ruta propia la OT tiene URL: se comparte por chat, se abre en otra
   * pestaña y el botón de atrás del navegador vuelve al tablero. Nada de eso
   * existía mientras el detalle era estado local de esta pantalla.
   */
  const abrirOrden = (o: OrdenListada) => navegar(`/produccion/${o.id}`);

  const todas = ordenes.datos ?? [];

  // El grupo y la búsqueda filtran del lado cliente sobre lo que el worker ya
  // devolvió — que ya viene filtrado por visibilidad. El filtro de estado sí va
  // al servidor, porque es el que puede achicar de verdad el conjunto.
  const texto = busqueda.trim().toLowerCase();
  const visibles = todas.filter((o) => {
    const enGrupo =
      grupo === null || (GRUPOS.find((g) => g.clave === grupo)?.estados.includes(o.estado) ?? true);
    return enGrupo && coincideConFiltros(o, { texto, impresoraId: impresoraFiltro });
  });

  // El orden se aplica sobre lo ya filtrado y del lado cliente: el listado
  // viene acotado del worker, así que ordenar acá evita una ida a la base por
  // cada click en un encabezado.
  const ordenadas = prefs.orden
    ? [...visibles].sort((a, b) => {
        const campo = prefs.orden?.campo as keyof OrdenListada;
        const x = String(a[campo] ?? "");
        const y = String(b[campo] ?? "");
        const cmp = x.localeCompare(y, "es", { numeric: true });
        return prefs.orden?.direccion === "desc" ? -cmp : cmp;
      })
    : visibles;

  // La página se recorta a lo que existe: filtrar estando en la 7 de un listado
  // que ahora tiene 2 dejaba la tabla vacía, con el paginador diciendo que hay
  // filas — y se leía como «el filtro no encontró nada».
  const paginaActual = paginaValida(pagina, ordenadas.length, POR_PAGINA);
  const enPantalla = paginar(ordenadas, paginaActual, POR_PAGINA);

  const alOrdenar = (campo: string) =>
    setPrefs((p) => ({
      ...p,
      orden: siguienteOrden(p.orden, campo),
    }));

  const filtrosActivos =
    (estado ? 1 : 0) + (grupo ? 1 : 0) + (texto ? 1 : 0) + (impresoraFiltro ? 1 : 0);

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Tablero de Producción"
        subtitulo="Monitorea el estado de los trabajos en curso."
        acciones={
          // `imprenta` monta la pantalla de alta pero NO la enlaza desde ningún
          // lado: se llega solo escribiendo la URL. El botón es una divergencia
          // deliberada (ADR 0053) — una pantalla a la que no se llega es lo
          // mismo que no tenerla.
          <>
            {can("produccion", "create") ? (
              <Boton onClick={() => navegar("/produccion/nueva")}>
                <Plus className="mr-2 inline h-4 w-4" aria-hidden="true" />
                Nueva OT
              </Boton>
            ) : null}
            <MenuDeOpciones
              vista={prefs.vista}
              alCambiarVista={(vista) => setPrefs((p) => ({ ...p, vista }))}
              mostrarTarjetas={prefs.mostrarTarjetas}
              alCambiarTarjetas={(v) => setPrefs((p) => ({ ...p, mostrarTarjetas: v }))}
            />
          </>
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}
      {ordenes.error ? <Aviso>{ordenes.error}</Aviso> : null}

      {/* Las tarjetas FILTRAN, no informan: es el comportamiento de `imprenta`.
          Y se pueden esconder desde el menú de opciones, como allá. */}
      {prefs.mostrarTarjetas ? (
        <TarjetasDeEstado
          tarjetas={[
            {
              clave: "todas",
              titulo: "Total OTs",
              valor: todas.length,
              tono: "neutro",
              activa: grupo === null,
              alClickear: () => setGrupo(null),
            },
            ...GRUPOS.map((g) => ({
              clave: g.clave,
              titulo: g.titulo,
              valor: todas.filter((o) => g.estados.includes(o.estado)).length,
              tono: TONO_DE_GRUPO[g.clave] ?? "neutro",
              activa: grupo === g.clave,
              alClickear: () => setGrupo(grupo === g.clave ? null : g.clave),
              // Los estados del grupo, que la tarjeta despliega al pasar el
              // mouse. Es como `imprenta` deja filtrar por un estado puntual sin
              // gastar una fila de filtros en algo que se usa de a ratos.
              subEstados: g.estados.map((e) => ({
                clave: e,
                // Con su etiqueta, no el enum: acá decía `titulo: e`, así que el
                // desglose mostraba `APROBACION_PENDIENTE` (ADR 0087). No se
                // veía leyendo porque solo aparece al pasar el mouse.
                titulo: etiquetaDeEstado(e),
                valor: todas.filter((o) => o.estado === e).length,
                activa: estado === e,
                alClickear: () => setEstado(estado === e ? "" : e),
              })),
            })),
          ]}
        />
      ) : null}

      <FilterBar
        busqueda={
          <CampoDeBusqueda
            valor={busqueda}
            alCambiar={setBusqueda}
            placeholder="Buscar por número, cliente, producto, estado..."
          />
        }
        filtrosActivos={filtrosActivos}
        alLimpiar={() => {
          setEstado("");
          setGrupo(null);
          setBusqueda("");
          setImpresoraFiltro("");
        }}
      >
        <select
          aria-label="Estado"
          value={estado}
          onChange={(e) => setEstado(e.target.value)}
          className="rounded-md border border-neutral-300 bg-neutral-50 px-2 py-1.5 text-neutral-900 text-sm"
        >
          <option value="">Todos los estados</option>
          {ESTADOS_DEL_FILTRO.map((e) => (
            <option key={e} value={e}>
              {etiquetaDeEstado(e)}
            </option>
          ))}
        </select>

        {/* El filtro por impresora es de `imprenta` y acá no existía: con seis
            equipos y treinta OTs, «qué tiene cargado la Epson 1» se contestaba
            leyendo la tabla entera. */}
        <select
          aria-label="Impresora"
          value={impresoraFiltro}
          onChange={(e) => setImpresoraFiltro(e.target.value)}
          className="rounded-md border border-neutral-300 bg-neutral-50 px-2 py-1.5 text-neutral-900 text-sm"
        >
          <option value="">Todas las impresoras</option>
          {(impresoras.datos ?? []).map((i) => (
            <option key={i.id} value={i.id}>
              {i.nombre}
            </option>
          ))}
        </select>
      </FilterBar>

      {/* Cuatro vistas del mismo trabajo. La tabla sirve para buscar una OT
          puntual; el kanban para ver dónde se amontona; las tarjetas para
          barrer el estado general; la lista para recorrer muchas seguidas. */}
      {prefs.vista === "tabla" ? (
        <DataTable
          datos={enPantalla}
          clave={(o) => o.id}
          cargando={ordenes.cargando}
          mensajeVacio="No hay órdenes para ese filtro"
          orden={prefs.orden}
          alOrdenar={alOrdenar}
          columnas={[
            {
              clave: "numero",
              ordenarPor: "numero",
              encabezado: "OT",
              render: (o) => (
                <span className="whitespace-nowrap font-mono text-sm">{o.numero}</span>
              ),
            },
            {
              // La consulta de la que salió, enlazada — como en `imprenta`. Va
              // el NÚMERO y no el id: acá el id es un UUID, y «#a3f2c1…» no lo
              // reconoce nadie.
              clave: "consulta",
              encabezado: "Consulta",
              render: (o) =>
                o.presupuestoId ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navegar(`/presupuestos?abrir=${o.presupuestoId}`);
                    }}
                    className="whitespace-nowrap font-mono text-brand-600 text-sm hover:underline"
                  >
                    {o.presupuestoNumero ?? "—"}
                  </button>
                ) : (
                  <span className="text-neutral-400">—</span>
                ),
            },
            { clave: "cliente", encabezado: "Cliente", render: (o) => o.cliente },
            {
              clave: "producto",
              ordenarPor: "producto",
              encabezado: "Producto",
              render: (o) => <span className="text-neutral-600">{o.producto}</span>,
            },
            {
              // Columna propia y no pegada al producto: `imprenta` la tiene
              // aparte y ordenable, que es como se busca «la tirada grande».
              clave: "cantidad",
              ordenarPor: "cantidad",
              encabezado: "Cantidad",
              render: (o) => <span className="text-neutral-600">{o.cantidad}</span>,
            },
            {
              clave: "estado",
              ordenarPor: "estado",
              encabezado: "Estado",
              render: (o) => <EstadoInline orden={o} alCambiar={recargar} />,
            },
            // Impresora y responsable van editables en la fila, en ese orden y
            // después del estado, como en el `JobBoard` de `imprenta`: asignar
            // es la operación más repetida del tablero y abrir el detalle para
            // eso es un paso de más por OT.
            {
              clave: "impresora",
              encabezado: "Impresora",
              render: (o) => (
                <ImpresoraInline
                  orden={o}
                  impresoras={impresoras.datos ?? []}
                  alAsignar={(impresoraId) => accionFila(() => asignarOrden(o.id, { impresoraId }))}
                />
              ),
            },
            {
              clave: "responsable",
              ordenarPor: "responsable",
              encabezado: "Responsable",
              render: (o) => (
                <ResponsableInline
                  orden={o}
                  usuarios={usuarios.datos ?? []}
                  alAsignar={(responsableId) =>
                    accionFila(() => asignarOrden(o.id, { responsableId }))
                  }
                />
              ),
            },
            {
              clave: "acciones",
              encabezado: "",
              className: "text-right",
              render: (o) => (
                <Boton variante="fantasma" onClick={() => abrirOrden(o)}>
                  Abrir
                </Boton>
              ),
            },
          ]}
        />
      ) : null}

      {/* El paginador va SOLO en la tabla: el kanban se lee entero por columna,
          y en tarjetas y lista se barre con el scroll. Es lo que hace
          `imprenta`. */}
      {prefs.vista === "tabla" && ordenadas.length > POR_PAGINA ? (
        <Paginacion
          pagina={paginaActual}
          tamano={POR_PAGINA}
          total={ordenadas.length}
          alCambiarPagina={setPagina}
        />
      ) : null}

      {/* La tabla ya se dibujó arriba, con su paginador: acá arranca la cadena
          de las OTRAS tres vistas. Sin este primer `null`, con la vista en
          tabla la cadena caía en su `else` y se pintaban DOS vistas, una
          debajo de la otra. */}
      {prefs.vista === "tabla" ? null : prefs.vista === "kanban" ? (
        <Kanban
          ordenes={ordenadas}
          alAbrir={abrirOrden}
          alMover={async (orden, destino) => {
            setError(null);
            if (exigeMotivo(orden.estado, destino, false)) {
              setCancelando({ orden, destino });
              return;
            }
            // Nunca fuerza (ADR 0098 B14): un arrastre fuera del flujo es el
            // pedido ordinario, y el 422 del worker dice por qué no.
            try {
              await cambiarEstadoOrden(orden.id, destino);
              await ordenes.recargar();
            } catch (err) {
              // La máquina de estados vive en el worker (ADR 0024): si la
              // transición es ilegal contesta 422 y se muestra el motivo, en vez
              // de replicar las reglas acá y desincronizarlas.
              setError(err instanceof Error ? err.message : "No se pudo mover");
            }
          }}
        />
      ) : ordenadas.length === 0 && !ordenes.cargando ? (
        <SinResultados mensaje="No hay órdenes que coincidan con los filtros." />
      ) : prefs.vista === "tarjetas" ? (
        <VistaTarjetas ordenes={ordenadas} alAbrir={abrirOrden} />
      ) : (
        <VistaLista ordenes={ordenadas} alAbrir={abrirOrden} />
      )}

      {cancelando ? (
        <DialogoCambioDeEstado
          numero={cancelando.orden.numero}
          estadoActual={cancelando.orden.estado}
          modo={{ tipo: "ordinario", destino: cancelando.destino }}
          onCerrar={() => setCancelando(null)}
          alConfirmar={async (destino, motivo) => {
            await cambiarEstadoOrden(cancelando.orden.id, destino, { motivo });
            await ordenes.recargar();
          }}
        />
      ) : null}
    </div>
  );
}
